import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const statusColors = {
  connected: { color: 'success', label: 'Connected' },
  pending: { color: 'warning', label: 'Pending' },
  disconnected: { color: 'error', label: 'Disconnected' },
  not_configured: { color: 'default', label: 'Not configured' },
};

const CustomerConfigs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [configs, setConfigs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/whatsapp/customer-configs/', {
        params: {
          search: searchTerm,
          status: statusFilter,
        },
      });
      
      if (response.data.success) {
        setConfigs(response.data.data || []);
      } else {
        setError('Failed to fetch customer configurations');
      }
    } catch (err) {
      console.error('Error fetching WhatsApp customer configs:', err);
      setError(err.response?.data?.error || 'Failed to fetch customer configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleRowClick = (row) => {
    navigate(`/whatsapp/customer-configs/${row.id}`, { state: { customer: row } });
  };

  const handleRefresh = () => {
    fetchConfigs();
  };

  const filteredConfigs = useMemo(() => {
    return configs;
  }, [configs]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Customers WhatsApp Configs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage WhatsApp Business configuration per customer
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 3,
            }}
          >
            <TextField
              size="small"
              placeholder="Search by customer name or..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  fetchConfigs();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="connected">Connected</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="disconnected">Disconnected</MenuItem>
                <MenuItem value="not_configured">Not configured</MenuItem>
              </Select>
            </FormControl>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button 
              variant="contained" 
              startIcon={<SearchIcon />} 
              onClick={fetchConfigs}
              disabled={loading}
            >
              Search
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone Number ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>WhatsApp Business Account</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : filteredConfigs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No customer WhatsApp configs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredConfigs.map((row) => {
                    const statusInfo = statusColors[row.status] || statusColors.not_configured;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => handleRowClick(row)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {row.customer_name}
                          </Typography>
                          {row.company_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.company_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{row.phone_number || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                            {row.phone_number_id || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.waba_name ? (
                            <>
                              <Typography variant="body2">{row.waba_name}</Typography>
                              {row.waba_id && (
                                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                  {row.waba_id}
                                </Typography>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusInfo.label}
                            color={statusInfo.color}
                            variant={row.status === 'not_configured' ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(row);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerConfigs;
